import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  ButtonGroup,
  Button,
  Pagination,
  Form,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import { FaTh, FaList } from "react-icons/fa";
import supabase from "../../../../supabaseClient";
import "./BlogHome.css";

function BlogHome() {
  const [blogs, setBlogs] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [view, setView] = useState("list");
  const blogsPerPage = 6;
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState("id");

  useEffect(() => {
    fetchBlogs();
  }, [searchTerm, sortOrder]);

  const fetchBlogs = async () => {
    try {
      let query = supabase.from("blogs").select("*, date");

      // Apply search term if provided
      if (searchTerm) {
        query = query.or(
          `title.ilike.%${searchTerm}%,author.ilike.%${searchTerm}%,tags.ilike.%${searchTerm}%`
        );
      }
      if (sortOrder === "id") {
        query = query.order("id", { ascending: true });
      } else if (sortOrder === "date") {
        query = query.order("date", { ascending: false });
      }

      const { data: blogs, error } = await query;
      if (error) {
        console.error("Error fetching blogs:", error);
      } else {
        // Add thumbnail URL to each blog
        const blogsWithThumbnails = blogs.map((blog) => ({
          ...blog,
          thumbnail: `https://oginzzlmzlozxkihihtv.supabase.co/storage/v1/object/public/blog-thumbnails/${blog.id}.webp`, // Adjust bucket name if needed
        }));
        setBlogs(blogsWithThumbnails || []);
      }
    } catch (err) {
      console.error("Unexpected error:", err);
    }
  };

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const startIndex = (currentPage - 1) * blogsPerPage;
  const endIndex = startIndex + blogsPerPage;
  const currentBlogs = blogs.slice(startIndex, endIndex);

  const totalPages = Math.ceil(blogs.length / blogsPerPage);

  const maxPagesToShow = 5;
  const startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
  const endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

  return (
    <Container>
      <Row className="mb-3">
        <Col md={6}>
          <Form.Control
            type="text"
            placeholder="Search by title, author, tags"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </Col>
        <Col md={4} className="text-end">
          <ButtonGroup>
            <Button
              variant={view === "list" ? "primary" : "outline-primary"}
              onClick={() => setView("list")}
              size="sm"
            >
              <FaList />
            </Button>
            <Button
              variant={view === "grid" ? "primary" : "outline-primary"}
              onClick={() => setView("grid")}
              size="sm"
            >
              <FaTh />
            </Button>
          </ButtonGroup>
        </Col>
        <Col md={2} className="text-end">
          <Form.Select
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            size="sm"
          >
            <option value="id">Sort by ID</option>
            <option value="date">Sort by Date</option>
          </Form.Select>
        </Col>
      </Row>
      <Row>
        {currentBlogs.map((blog) =>
          view === "list" ? (
            <Col md={12} key={blog.id} className="mb-4">
              <Card className="blog-card list-view">
                <Row>
                  <Col md={4}>
                    <Card.Img
                      src={`https://oginzzlmzlozxkihihtv.supabase.co/storage/v1/object/public/blog-thumbnails/${blog.id}.webp`}
                      alt={`${blog.title} Thumbnail`}
                      className="blog-card-img"
                    />
                  </Col>
                  <Col md={8}>
                    <Card.Body>
                      <Card.Text className="blog-card-tags">
                        {new Date(blog.date).toLocaleDateString("en-GB", {
                          month: "long",
                          day: "2-digit",
                        })}
                      </Card.Text>
                      <Card.Title className="blog-card-title">
                        {blog.title}
                      </Card.Title>
                      <Card.Text className="blog-card-content">
                        {blog.content.substring(0, 50)}...
                      </Card.Text>
                      <Link to={`/blog/${blog.id}`} className="btn btn-primary">
                        Read More
                      </Link>
                    </Card.Body>
                  </Col>
                </Row>
              </Card>
            </Col>
          ) : (
            <Col md={4} key={blog.id} className="mb-4">
              <Card className="blog-card">
                <Card.Img
                  variant="top"
                  src={blog.thumbnail}
                  className="blog-card-img"
                />
                <Card.Body>
                  <Card.Text className="blog-card-tags">
                    {new Date(blog.date).toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "long",
                      year: "numeric",
                    })}
                  </Card.Text>
                  <Card.Title className="blog-card-title blog-title">
                    {blog.title}
                  </Card.Title>
                  <Card.Text className="blog-card-content">
                    {blog.content.substring(0, 30)}...
                  </Card.Text>
                  <Link to={`/blog/${blog.id}`} className="btn btn-primary">
                    Read More
                  </Link>
                </Card.Body>
              </Card>
            </Col>
          )
        )}
      </Row>
      <Pagination className="justify-content-center">
        <Pagination.First
          onClick={() => handlePageClick(1)}
          disabled={currentPage === 1}
        />
        <Pagination.Prev
          onClick={() => handlePageClick(currentPage - 1)}
          disabled={currentPage === 1}
        />
        {[...Array(endPage - startPage + 1)].map((_, index) => {
          const pageNumber = startPage + index;
          return (
            <Pagination.Item
              key={pageNumber}
              active={pageNumber === currentPage}
              onClick={() => handlePageClick(pageNumber)}
            >
              {pageNumber}
            </Pagination.Item>
          );
        })}
        <Pagination.Next
          onClick={() => handlePageClick(currentPage + 1)}
          disabled={currentPage === totalPages}
        />
        <Pagination.Last
          onClick={() => handlePageClick(totalPages)}
          disabled={currentPage === totalPages}
        />
      </Pagination>
    </Container>
  );
}

export default BlogHome;
